import { ApolloError, useLazyQuery, useMutation } from '@apollo/client';
import {
	Button,
	Icon,
	SettingsGroup,
	SettingsItemAutocomplete,
	SettingsItemCheckbox,
	SettingsItemColor,
	SettingsItemTextField,
} from '@elipssolution/harfang';
import { mdiAlertCircle, mdiCheck, mdiPencil, mdiPlus } from '@mdi/js';
import { Typography, styled } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import SettingCustomerFileTagNameField from './SettingCustomerFileTagNameField';
import { useConfirmationDialogContext } from '../../../../../../components/ConfirmationDialogProvider';
import SettingsDialogPage from '../../../../../../components/SettingsDialogPage';
import { useSettingsDialog } from '../../../../../../hooks/useSettingsDialog';
import { CustomerFileWithDomainType } from '../../../../../../types/customerFile';
import { generateErrorInformations } from '../../../../../../utils/errorHandler';
import { SUCCESS_ANIMATION_DELAY } from '../../../../../../utils/statusAnimationDelay';
import {
	CREATE_SETTING_CUSTOMER_FILE_TAG,
	CreateSettingCustomerFileType,
	FETCH_SETTING_CUSTOMER_FILE_TAG,
	FetchSettingCustomerFileTagType,
	REMOVE_SETTING_CUSTOMER_FILE_TAG,
	UPDATE_SETTING_CUSTOMER_FILE_TAG,
	UpdateSettingCustomerFileTagType,
} from '../../../../api/settingCustomerFileTag';
import { FETCH_STORAGE_UPLOADS, FetchStorageUploadsType } from '../../../../api/settingStorageUpload';
import { DocumentConnectorCodeEnum } from '../../../../types/documentConnectorCode';
import { SettingStorageUploadType } from '../../../../types/settingStorage';
import { SettingTagByCustomerFileType } from '../../../../types/settingTag';

const ErrorWrapper = styled('div')(({ theme }) => ({
	overflow: 'auto',

	minHeight: 100,
	minWidth: '50%',
	maxWidth: '75%',

	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'center',
	alignItems: 'center',

	padding: theme.spacing(1),
	gap: theme.spacing(1),
	margin: '16px auto 0 auto',

	color: theme.palette.error.main,
	backgroundColor: `${theme.palette.error.main}1A`,
	borderRadius: theme.shape.borderRadius,
}));

const ButtonWrapper = styled('div')({
	display: 'flex',
	flexDirection: 'row-reverse',
	justifyContent: 'space-between',
});

type CustomerFileTagsFormProps = {
	customerFile: CustomerFileWithDomainType;
	tag?: SettingTagByCustomerFileType;
};

type FormFieldType = Pick<
	SettingTagByCustomerFileType,
	'color' | 'isEnabled' | 'name' | 'storageUpload' | 'info' | 'configField'
>;

const CustomerFileTagsForm = ({ customerFile, tag }: CustomerFileTagsFormProps) => {
	const { id: customerFileId } = customerFile;
	const { id: initialTagId, name: initialTagName, isInherited, isOverload } = tag ?? {};

	const { back, onHasDirtyFormChange, onCloseConfirmationTitleChange } = useSettingsDialog();
	const { confirmAction } = useConfirmationDialogContext();

	const [isCreateTagSucceeded, setIsCreateTagSucceeded] = useState(false);
	const [isUpdateTagSucceeded, setIsUpdateTagSucceeded] = useState(false);
	const [isOverloadTagSucceeded, setIsOverloadTagSucceeded] = useState(false);

	const {
		clearErrors,
		control,
		formState: { isDirty, isValid },
		handleSubmit,
		setError,
		watch,
		reset,
	} = useForm<FormFieldType>();

	const [tagId, setTagId] = useState(initialTagId);
	const [tagName, setTagName] = useState(initialTagName);
	const [isTagOverload, setIsTagOverload] = useState(isOverload);
	const [isTagInherited, setIsTagInherited] = useState(isInherited);
	const [isTagArchived, setIsTagArchived] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string>();

	const formStorageUpload = watch('storageUpload');
	const { connector, tagFieldLabel } = formStorageUpload ?? {};

	const [fetchSettingTag, { loading: isSettingCustomerFileTagLoading }] = useLazyQuery<FetchSettingCustomerFileTagType>(
		FETCH_SETTING_CUSTOMER_FILE_TAG,
		{
			onCompleted: ({ document_settingCustomerFileTag: { name } }) => {
				setTagName(name);
			},
			onError: (fetchError) =>
				setErrorMessage(
					generateErrorInformations({ error: fetchError, resource: 'document_settingCustomerFileTag' }).message,
				),
		},
	);

	const [fetchSettingStorageUploads] = useLazyQuery<FetchStorageUploadsType>(FETCH_STORAGE_UPLOADS);

	const settingStorageUploadsDataSource = useCallback(
		async (
			limit: number,
			offset: number,
			search?: string,
		): Promise<{
			items: SettingStorageUploadType[];
			count: number;
		}> => {
			const { data, error } = await fetchSettingStorageUploads({
				variables: {
					page: {
						limit,
						offset,
					},
					search,
				},
			});

			if (error) {
				throw error;
			}

			const {
				document_settingStorageUploads: { count = 0, items = [] },
			} = data ?? {
				document_settingStorageUploads: {},
			};

			return {
				items,
				count,
			};
		},
		[fetchSettingStorageUploads],
	);

	const [createTag, { loading: isCreateTagLoading }] = useMutation<CreateSettingCustomerFileType>(
		CREATE_SETTING_CUSTOMER_FILE_TAG,
		{
			onCompleted: ({ document_createSettingCustomerFileTag: { id } }) => {
				if (tagId) {
					setIsTagOverload(true);
					setIsTagInherited(false);
					setIsOverloadTagSucceeded(true);
					setTimeout(() => setIsOverloadTagSucceeded(false), SUCCESS_ANIMATION_DELAY);
				} else {
					setIsCreateTagSucceeded(true);
					setTimeout(() => setIsCreateTagSucceeded(false), SUCCESS_ANIMATION_DELAY);
				}

				setTagId(id);
			},
		},
	);

	const [updateTag, { loading: isUpdateTagLoading }] = useMutation<UpdateSettingCustomerFileTagType>(
		UPDATE_SETTING_CUSTOMER_FILE_TAG,
		{
			onCompleted: () => {
				setIsUpdateTagSucceeded(true);
				setTimeout(() => setIsUpdateTagSucceeded(false), SUCCESS_ANIMATION_DELAY);
			},
		},
	);

	const [removeTag, { loading: isRemoveTagLoading }] = useMutation(REMOVE_SETTING_CUSTOMER_FILE_TAG, {
		onCompleted: back,
		onError: (error: ApolloError) =>
			setErrorMessage(generateErrorInformations({ error, resource: 'document_removeSettingCustomerFileTag' })?.message),
	});

	const applyArchivedTag = useCallback(
		(tagArchivedId: string) => {
			setIsTagArchived(true);

			fetchSettingTag({
				variables: {
					settingCustomerFileTagId: tagArchivedId,
				},
			}).catch((error) => {
				throw error;
			});
		},
		[fetchSettingTag],
	);

	const isLoading = useMemo(
		() => isSettingCustomerFileTagLoading || isCreateTagLoading || isUpdateTagLoading,
		[isSettingCustomerFileTagLoading, isCreateTagLoading, isUpdateTagLoading],
	);

	const isMutationButtonDisabled = useMemo(
		() => isRemoveTagLoading || !isValid || (!isDirty && !isTagArchived),
		[isDirty, isRemoveTagLoading, isTagArchived, isValid],
	);

	const mutationButtonName = useMemo(() => {
		if (isTagArchived) return 'Confirmer';

		if (tagId) {
			if (isTagInherited) return 'Surcharger le tag';
			if (isCreateTagSucceeded) return 'Tag ajouté';
			if (isUpdateTagSucceeded) return 'Tag modifié';
			if (isOverloadTagSucceeded) return 'Tag surchargé';

			return 'Modifier';
		}

		return 'Ajouter';
	}, [isCreateTagSucceeded, isOverloadTagSucceeded, isTagArchived, isTagInherited, isUpdateTagSucceeded, tagId]);

	const startIcon = useMemo(
		() => (isTagArchived ? <Icon path={mdiCheck} /> : <Icon path={tagId ? mdiPencil : mdiPlus} />),
		[isTagArchived, tagId],
	);

	const onSubmit = useCallback(
		(values: FormFieldType) => {
			const { color, isEnabled, name, storageUpload, info, configField } = values;
			const { id: storageUploadId } = storageUpload || {};

			const modeMutationInfoMap = new Map([
				[
					'create',
					{
						mutation: createTag,
						mutationName: 'createSettingCustomerFileTag',
						inputValues: {
							color,
							customerFileId,
							isEnabled,
							name,
							storageUploadId,
							configField,
							...(tagId && { overriddenTagId: tagId }),
							info,
						},
					},
				],
				[
					'update',
					{
						mutation: updateTag,
						mutationName: 'updateSettingCustomerFileTag',
						inputValues: {
							id: tagId,
							color,
							isEnabled,
							name,
							storageUploadId,
							configField,
							...(isTagArchived && { isArchived: false }),
							info: info || null,
						},
					},
				],
			]);

			const {
				mutation,
				mutationName = '',
				inputValues,
			} = modeMutationInfoMap.get(!tagId || (isTagInherited && tagId) ? 'create' : 'update') ?? {};

			mutation?.({
				variables: {
					[`${mutationName}Input`]: inputValues,
				},
			})
				.then(({ data }) => {
					if (!data) return;

					setIsTagArchived(false);

					reset(values);

					setTagName(name);
				})
				.catch((mutationError: ApolloError) =>
					setErrorMessage(generateErrorInformations({ error: mutationError, resource: mutationName }).message),
				);
		},
		[createTag, reset, updateTag, customerFileId, isTagInherited, isTagArchived, tagId],
	);

	const handleRemoveTagButtonClick = async () => {
		if (!tagName) return undefined;

		const isRemovalConfirmed = await confirmAction({
			title: `Êtes-vous sûr de vouloir supprimer le tag ${tagName || ''} ?`,
			confirmButtonDetails: {
				color: 'error',
				label: !isTagInherited && isTagOverload ? 'Revenir au tag domaine' : 'Supprimer',
			},
		});

		if (!isRemovalConfirmed) return null;

		return removeTag({
			variables: {
				removeSettingCustomerFileTagId: tagId,
			},
		});
	};

	useEffect(() => reset(tag), [reset, tag]);

	useEffect(() => {
		onHasDirtyFormChange(isDirty);
		onCloseConfirmationTitleChange(`Êtes-vous sûr de vouloir quitter la ${tag ? 'modification' : 'création'} du tag ?`);
	}, [tag, isDirty, onCloseConfirmationTitleChange, onHasDirtyFormChange]);

	return (
		<SettingsDialogPage title={tagName ?? "Ajout d'un tag"}>
			<SettingsGroup>
				<SettingCustomerFileTagNameField
					clearErrors={clearErrors}
					control={control}
					customerFileId={customerFile.id}
					initialValue={tagName}
					onApplyArchivedTag={applyArchivedTag}
					setError={setError}
					tagId={tagId}
				/>
				<Controller
					control={control}
					name="info"
					render={({ field: { onChange, ...field }, fieldState: { error } }) => (
						<SettingsItemTextField
							{...field}
							description="Informations du tag d'entreprise."
							helperText={error?.message ?? ' '}
							invalid={!!error}
							label="Information"
							onChange={onChange}
						/>
					)}
				/>

				<Controller
					name="color"
					control={control}
					render={({ field: { value, ...field } }) => (
						<SettingsItemColor
							{...field}
							label="Couleur"
							description="Couleur du tag."
							selectedColorCode={value}
							required
						/>
					)}
					rules={{ required: true }}
				/>
				<Controller
					name="storageUpload"
					control={control}
					render={({ field }) => (
						<SettingsItemAutocomplete<SettingStorageUploadType>
							{...field}
							dataSource={settingStorageUploadsDataSource}
							description="Emplacement attaché au tag d'entreprise."
							getOptionLabel={(option) => option?.name || ''}
							label="Emplacement"
							required
						/>
					)}
					rules={{ required: true }}
				/>
				<Controller
					defaultValue
					name="isEnabled"
					control={control}
					render={({ field: { value, ...field } }) => (
						<SettingsItemCheckbox
							{...field}
							checked={value}
							description="Si coché, le tag d'entreprise est actif."
							disabled={isLoading}
							label="Actif"
						/>
					)}
				/>

				{(connector === DocumentConnectorCodeEnum.ACD || connector === DocumentConnectorCodeEnum.DOCUWARE) &&
					!!tagFieldLabel && (
						<Controller
							control={control}
							name="configField"
							render={({ field: { onChange, ...field } }) => (
								<SettingsItemTextField {...field} label={tagFieldLabel} onChange={(value) => onChange(value ?? null)} />
							)}
						/>
					)}
			</SettingsGroup>

			<ButtonWrapper>
				<Button
					disabled={isMutationButtonDisabled}
					onClick={handleSubmit(onSubmit)}
					startIcon={startIcon}
					variant="contained"
				>
					{mutationButtonName}
				</Button>

				{tagId && !isTagInherited && !isTagArchived && (
					<Button color="error" disabled={isLoading} onClick={handleRemoveTagButtonClick} variant="outlined">
						{!isTagInherited && isTagOverload ? 'Revenir au tag domaine' : 'Supprimer'}
					</Button>
				)}
			</ButtonWrapper>

			{errorMessage && (
				<ErrorWrapper>
					<Icon path={mdiAlertCircle} />
					<Typography>{errorMessage}</Typography>
				</ErrorWrapper>
			)}
		</SettingsDialogPage>
	);
};

export default CustomerFileTagsForm;
